import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  prefersReducedMotion,
  debounce,
  isLowPowerDevice,
  lockScroll,
  unlockScroll,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 * @property {HTMLElement} gallery - The gallery scroll container.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails', 'gallery'];

  #highResImagesLoaded = new WeakSet();
  #pendingImages = new WeakSet();
  #opening = false;
  #closing = false;
  #closeRequested = false;
  #activeIndex = 0;
  #opener;

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('scroll', this.handleScroll);
    this.refs.gallery.addEventListener('scroll', this.handleScroll);
    this.refs.dialog.addEventListener('cancel', this.#handleCancel);
    this.refs.dialog.addEventListener('close', this.#handleClose);
  }

  disconnectedCallback() {
    this.refs.dialog.removeEventListener('scroll', this.handleScroll);
    this.refs.gallery.removeEventListener('scroll', this.handleScroll);
    this.refs.dialog.removeEventListener('cancel', this.#handleCancel);
    this.refs.dialog.removeEventListener('close', this.#handleClose);
    this.handleScroll.cancel();
    unlockScroll(this.refs.dialog);
    super.disconnectedCallback();
  }

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event?.preventDefault();

    const { dialog, media, thumbnails } = this.refs;
    const targetImage = media[index];
    if (!targetImage || dialog.open || this.#opening || this.#closing) return;

    this.#opening = true;
    this.#opener = event?.target instanceof Element ? event.target.closest('button, [tabindex]') : document.activeElement;

    const sourceImage = event?.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;
    const preview = sourceImage?.querySelector('img.product-media__image');
    const fullImage = targetImage.querySelector('img.product-media__image');
    if (preview instanceof HTMLImageElement && fullImage instanceof HTMLImageElement && !fullImage.complete) {
      fullImage.removeAttribute('srcset');
      fullImage.src = preview.currentSrc || preview.src;
    }
    if (fullImage instanceof HTMLImageElement) fullImage.loading = 'eager';
    for (const image of thumbnails.querySelectorAll('img')) image.loading = 'eager';

    const open = () => {
      lockScroll(dialog);
      dialog.showModal();
      this.selectThumbnail(index, { behavior: 'instant' });
    };

    try {
      if (!supportsViewTransitions() || isLowPowerDevice() || prefersReducedMotion() || !sourceImage) {
        open();
        return;
      }

      const itemTransitionName = 'gallery-item-open';
      sourceImage.style.setProperty('view-transition-name', itemTransitionName);

      const focalPoint = sourceImage.dataset.focalPoint;
      if (focalPoint) document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);

      await startViewTransition(() => {
        open();
        sourceImage.style.removeProperty('view-transition-name');
        targetImage.style.setProperty('view-transition-name', itemTransitionName);
      });
    } finally {
      document.documentElement.style.removeProperty('--gallery-media-focal-point');
      sourceImage?.style.removeProperty('view-transition-name');
      targetImage.style.removeProperty('view-transition-name');
      this.#opening = false;
      if (!dialog.open) unlockScroll(dialog);
      if (this.#closeRequested) {
        this.#closeRequested = false;
        this.close();
      }
    }
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(image) || this.#pendingImages.has(image)) return false;

    this.#pendingImages.add(image);
    const newImage = /** @type {HTMLImageElement} */ (image.cloneNode(false));
    newImage.loading = 'eager';
    newImage.decoding = 'async';
    newImage.removeAttribute('srcset');
    newImage.removeAttribute('sizes');
    newImage.src = highResolutionUrl;
    newImage
      .decode()
      .then(() => {
        if (!image.isConnected || !this.contains(image)) return;
        image.replaceWith(newImage);
        this.#highResImagesLoaded.add(newImage);
      })
      .catch(() => {})
      .finally(() => this.#pendingImages.delete(image));
    return true;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(() => {
    const { dialog, media, thumbnails } = this.refs;
    if (!dialog.open || this.#opening || this.#closing) return;

    const mostVisibleElement = getMostVisibleElement(media, dialog);
    if (!mostVisibleElement) return;
    const activeIndex = media.indexOf(mostVisibleElement);
    this.#activeIndex = activeIndex;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.loadHighResolutionImage(mostVisibleElement);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;
    if (this.#opening) {
      this.#closeRequested = true;
      return;
    }
    if (!dialog.open || this.#closing) return;
    this.#closing = true;

    const mostVisibleElement = getMostVisibleElement(media, dialog) || media[this.#activeIndex];
    const activeIndex = media.indexOf(mostVisibleElement);
    const itemTransitionName = `gallery-item-close`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slide = [mediaGallery?.slideshow?.slides?.[activeIndex], mediaGallery?.media?.[activeIndex]].find(
      (element) => element && element.getBoundingClientRect().width > 0
    );

    try {
      if (!supportsViewTransitions() || isLowPowerDevice() || prefersReducedMotion() || !slide || !mostVisibleElement) {
        this.closeDialog();
        return;
      }
      const focalPoint = slide.dataset.focalPoint;
      if (focalPoint) document.documentElement.style.setProperty('--gallery-media-focal-point', focalPoint);
      mostVisibleElement.style.setProperty('view-transition-name', itemTransitionName);

      await startViewTransition(() => {
        mostVisibleElement.style.removeProperty('view-transition-name');
        slide.style.setProperty('view-transition-name', itemTransitionName);
        this.closeDialog();
      });
    } finally {
      mostVisibleElement?.style.removeProperty('view-transition-name');
      slide?.style.removeProperty('view-transition-name');
      document.documentElement.style.removeProperty('--gallery-media-focal-point');
      this.#closing = false;
    }
  }

  closeDialog() {
    const { dialog } = this.refs;
    if (!dialog.open) return;
    dialog.close();
  }

  #handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };

  #handleClose = () => {
    this.handleScroll.cancel();
    unlockScroll(this.refs.dialog);
    window.dispatchEvent(new DialogCloseEvent());
    if (this.#opener instanceof HTMLElement && this.#opener.isConnected) this.#opener.focus({ preventScroll: true });
  };

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const { media } = this.refs;
    if (!media[index]) return;

    this.loadHighResolutionImage(media[index]);
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    if (isNaN(index) || index < 0 || index >= this.refs.media.length) return;

    const { dialog, media, thumbnails, gallery } = this.refs;
    const targetThumbnail = thumbnails.children[index];
    this.#activeIndex = Number(index);

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === index}`);
    });

    if (targetThumbnail instanceof HTMLElement) {
      scrollIntoView(targetThumbnail, {
        ancestor: thumbnails,
        behavior: options.behavior,
        block: 'center',
        inline: 'center',
      });
    }

    const targetImage = media[index];

    if (targetImage) {
      scrollIntoView(targetImage, {
        ancestor: gallery.scrollWidth > gallery.clientWidth ? gallery : dialog,
        behavior: options.behavior,
        block: 'start',
        inline: 'start',
      });

      this.loadHighResolutionImage(targetImage);
    }
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @param {HTMLElement} viewport - The visible gallery viewport.
 * @returns {HTMLElement | undefined} The most visible element.
 */
function getMostVisibleElement(elements, viewport) {
  const bounds = viewport.getBoundingClientRect();
  let mostVisible;
  let largestArea = 0;
  for (const element of elements) {
    const rect = element.getBoundingClientRect();
    const width = Math.max(0, Math.min(rect.right, bounds.right) - Math.max(rect.left, bounds.left));
    const height = Math.max(0, Math.min(rect.bottom, bounds.bottom) - Math.max(rect.top, bounds.top));
    const area = width * height;
    if (area > largestArea) {
      mostVisible = element;
      largestArea = area;
    }
  }
  return mostVisible;
}
